import * as React from 'react';
import './metadataFormFieldGroup.scss';
import { Metadata, Schema, SchemaField } from '../../../types/Metadata';
import { Field } from '../metadataFormField';
import { Arrow } from 'rcd-cpp-ui/ui/icon';
import { ValidationMode } from '../../../enums/ValidationMode';
import { FormUpdater } from '../../../types/Field';
import { UserSettings } from '../../../types/UserSetting';
import { getFieldsToDisplay, getShowFieldsOption } from '../../../utils';
import { FieldGroup } from '../../../enums/FieldGroup';
import { useSelector } from '../../../utils/useSelector';
import { useActions } from '../metadataFormActions';
import { AppRefId } from '../../../utils/constants';

type FieldGroupProps = {
  groupKey?: string;
  fields?: SchemaField[];
  data: Partial<Metadata>;
  impliedDataProps?: any;
  schema: Schema;
  schemas?: Schema[];
  disabled?: boolean;
  readonlyFields?: string[];
  updater: FormUpdater;
  shouldValidate?: ValidationMode;
  pushResponse?: any;
  isModal?: boolean;
  field?: SchemaField;
  userSettings?: UserSettings;
  children?: React.ReactNode;
};

export const MetadataFormFieldGroup = ({
  fields: propFields,
  data,
  impliedDataProps,
  schema,
  schemas,
  //disabled,
  readonlyFields,
  updater,
  shouldValidate,
  pushResponse,
  isModal = false,
  field,
  userSettings,
  children
}: FieldGroupProps) => {
  const allErrorFields = useSelector(s => s.form?.allErrorFields);
  const fields = React.useMemo(() => {
    if (propFields) return propFields;
    if ((field as any)?.childFields) {
      return (field as any).childFields;
    }

    if (field && schema) {
      const showFieldsOption = getShowFieldsOption(data, userSettings);
      const allDisplayFields = getFieldsToDisplay(
        schema,
        data,
        showFieldsOption,
        impliedDataProps,
        Array.from(allErrorFields ?? [])
      );

      let filteredFields = allDisplayFields
        .filter(f => f.parentField === field.field)
        .sort((a, b) => {
          const orderA = a.order ?? Number.MAX_SAFE_INTEGER;
          const orderB = b.order ?? Number.MAX_SAFE_INTEGER;
          return orderA - orderB;
        });

      return filteredFields;
    }

    return [];
  }, [
    propFields,
    field,
    schema,
    data,
    impliedDataProps,
    userSettings,
    pushResponse,
    allErrorFields
  ]);

  const title = field?.name;

  const hasErrors = React.useMemo(() => {
    if (!allErrorFields || allErrorFields.size === 0) return false;
    return fields.some(field => allErrorFields.has(field.key));
  }, [fields, allErrorFields]);

  const { setCollapsedAccordions } = useActions();
  const collapsedAccordions: string[] | undefined = useSelector(
    s => s.planning?.userSettings?.[AppRefId]?.props?.collapsedAccordions
  );

  const [isOpen, setIsOpen] = React.useState(() => {
    if (collapsedAccordions !== undefined && field?.field) {
      return !collapsedAccordions.includes(field.field);
    }
    return field?.field != FieldGroup.AdditionalItemDetails;
  });

  React.useEffect(() => {
    if (hasErrors) {
      setIsOpen(true);
    }
  }, [hasErrors]);

  const handleToggle = () => {
    const newIsOpen = !isOpen;
    setIsOpen(newIsOpen);
    if (field?.field) {
      const currentCollapsed = collapsedAccordions ?? [];
      const updatedCollapsed = newIsOpen
        ? currentCollapsed.filter(k => k !== field!.field)
        : [...currentCollapsed.filter(k => k !== field!.field), field!.field];
      setCollapsedAccordions(updatedCollapsed);
    }
  };

  if (!fields.length) return null;

  return (
    <div
      className={`field-group ${
        field?.field == FieldGroup.AdditionalItemDetails
          ? 'field-group-additionalitemdetails'
          : ''
      } ${isModal ? 'modal-style' : ''} ${isOpen ? 'expanded' : 'collapsed'}`}
    >
      <div
        className="field-group-header"
        onClick={handleToggle}
        style={{ cursor: 'pointer' }}
      >
        <div className="field-group-title">
          <span className={`field-group-icon ${isOpen ? 'open' : 'closed'}`}>
            <Arrow direction={isOpen ? 'down' : 'right'} type="solid" />
          </span>
          <span>{title}</span>
        </div>
        <span className="field-group-count">({fields.length})</span>
      </div>
      {isOpen && (
        <div className="field-group-content">
          {fields.map(field => (
            <Field
              key={field.field}
              field={field}
              data={data}
              impliedDataProps={impliedDataProps}
              schema={schema}
              schemas={schemas}
              disabled={readonlyFields?.includes(field.key)}
              updater={updater}
              shouldValidate={shouldValidate}
              pushResponse={pushResponse}
            />
          ))}
          {children}
        </div>
      )}
    </div>
  );
};
